import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from "@mui/material";

interface AccessibilityModalProps {
  open: boolean;
  onClose: () => void;
}

export const AccessibilityModal: React.FC<AccessibilityModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: "bold" }}>Accessibility Statement</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1">
            <strong>Effective Date: April 16, 2026</strong>
          </Typography>
          <Typography variant="body1">
            Obsidian Ridge is committed to making our website and services accessible to everyone,
            including people with disabilities. We design and develop with accessibility in mind and
            are continually working to improve.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Conformance Standard
          </Typography>
          <Typography variant="body2">
            We target conformance with the{" "}
            <strong>Web Content Accessibility Guidelines (WCAG) 2.1, Level AA</strong>. We are
            partially conformant - some areas may not yet fully meet every criterion, but we
            actively identify and remediate gaps.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            What We Do
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: "24px" }}>
            <li>
              <Typography variant="body2">
                Use semantic HTML elements for document structure and navigation
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Provide keyboard navigation and visible focus indicators for all interactive
                elements
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Maintain sufficient color contrast ratios for text and UI components
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Include descriptive alt text on meaningful images and label decorative images
                appropriately
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Use ARIA attributes where semantic HTML alone is insufficient
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Support light and dark color modes to accommodate user preferences
              </Typography>
            </li>
          </ul>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Known Limitations
          </Typography>
          <Typography variant="body2">
            Some third-party embedded content (e.g., YouTube videos, Stripe checkout) is outside our
            direct control and may not meet all WCAG 2.1 AA criteria. We choose vendors that
            prioritize accessibility and monitor for improvements.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Technologies
          </Typography>
          <Typography variant="body2">
            This site relies on HTML, CSS, JavaScript, and WAI-ARIA to function with browsers and
            assistive technologies such as screen readers.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Feedback
          </Typography>
          <Typography variant="body2">
            If you encounter an accessibility barrier or have suggestions, please contact us:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Email:{" "}
            <Link href="mailto:accessibility@obsidianridge.ai">accessibility@obsidianridge.ai</Link>
          </Typography>
          <Typography variant="body2">
            We aim to respond to accessibility feedback within 2 business days.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
